import { useCallback, useEffect, useMemo } from "react";
import { CardNotification, Link, Pager } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import TermSelect from "@modules/vacancies/Groups/term-select/TermSelect";
import type { CubeQuery, Term } from "@models/ISchema";
import { getAnnualPeriod, getDefaultAnnualPeriod } from "../../adapters";
import { paginateLeagues } from "../../lib";
import { annualScheduleDefaults, useAnnualScheduleStore } from "../../store";
import { usePackageAnnualSchedule } from "../../hooks/use-package-annual-schedule";
import type { AnnualScheduleContext } from "../../types";
import AnnualScheduleMatrix from "../../components/annual-schedule-matrix";
import AnnualScheduleDrawer from "../../components/annual-schedule-drawer";
import AnnualScheduleSkeleton from "../../components/annual-schedule-skeleton";
import styles from "./packageAnnualSchedule.module.scss";

/** Data and request state supplied by the package dimension's shared summary query. */
interface Props {
  context: AnnualScheduleContext;
  scopeKey: string;
  cube: CubeQuery | null;
  /** The package's academic term, selected until the user picks a period. */
  defaultTerm?: Term | null;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
}

/**
 * Presents the annual package matrix, its controls, and the selected cell's details.
 *
 * @param props - Shared package data, scoped UI context, and the summary retry action.
 * @returns The annual package view with a drawer that stays mounted while closed.
 *
 * @remarks This view derives rows and weeks from the parent's package response. Only
 * UI preferences and the selected cell live in the feature store; detail data is
 * requested separately when a valid populated cell opens the drawer. The period is
 * the package term until the user picks one, which then takes precedence for the scope.
 */
export default function PackageAnnualSchedule({
  context,
  scopeKey,
  cube,
  defaultTerm,
  loading,
  error,
  refresh,
}: Props) {
  const { t } = useTranslation();
  const store = useAnnualScheduleStore();
  const state = store.scopeKey === scopeKey ? store : annualScheduleDefaults;
  const { hasSelectedPeriod, selectedCell, page, pageSize } = state;
  const defaultPeriod = useMemo(() => getDefaultAnnualPeriod(defaultTerm), [defaultTerm]);
  const selectedPeriod = hasSelectedPeriod ? state.selectedPeriod : defaultPeriod;
  // On a direct load the package term is unknown until the first response arrives.
  const isPeriodPending = !hasSelectedPeriod && !defaultTerm && !cube && loading;
  const { groups, weeks } = usePackageAnnualSchedule(cube, selectedPeriod);
  const paginated = useMemo(
    () => paginateLeagues(groups, page, pageSize),
    [groups, page, pageSize],
  );
  const prefix = "dimension.package.annual";
  const allWeeksOption = useMemo(
    () => ({ label: t(`${prefix}.all-weeks`), value: "all_weeks" }),
    [t],
  );
  const periodOption = useMemo(
    () =>
      selectedPeriod ? { label: selectedPeriod.label, value: selectedPeriod.id } : allWeeksOption,
    [selectedPeriod, allWeeksOption],
  );
  const group = paginated.groups.find(item => item.id === selectedCell?.leagueId);
  const week = weeks.find(item => item.id === selectedCell?.intervalId);
  const isDrawerOpen =
    !!selectedCell &&
    !!group &&
    !!week &&
    group.rows.some(row => row.id === selectedCell.componentId);
  const closeDrawer = useCallback(() => store.selectCell(null), [store.selectCell]);

  useEffect(() => {
    store.enter(scopeKey);
  }, [scopeKey, store.enter]);
  useEffect(
    () => () => {
      useAnnualScheduleStore.getState().selectCell(null);
    },
    [],
  );
  useEffect(() => {
    if (!loading && !error && paginated.page !== page) store.setPage(paginated.page);
  }, [loading, error, paginated.page, page, store.setPage]);
  useEffect(() => {
    if (
      !loading &&
      !error &&
      selectedCell &&
      (!group || !week || !group.rows.some(row => row.id === selectedCell.componentId))
    )
      closeDrawer();
  }, [loading, error, selectedCell, group, week, closeDrawer]);

  const selectPeriod = (term?: Term) => store.setPeriod(getAnnualPeriod(term));

  return (
    <section className={styles.packageAnnualSchedule} aria-label={t(`${prefix}.title`)}>
      <div className={styles.packageAnnualSchedule_controls}>
        <TermSelect
          key={scopeKey}
          className={styles.packageAnnualSchedule_period}
          defaultTerm={isPeriodPending ? undefined : periodOption}
          externalOptions={[allWeeksOption]}
          isClearable={false}
          isDisabled={isPeriodPending}
          isLoading={isPeriodPending}
          onSelectTerm={selectPeriod}
        />
      </div>
      {loading ? (
        <AnnualScheduleSkeleton groups={paginated.groups} weeks={weeks} />
      ) : error ? (
        <div role="alert">
          <CardNotification
            state="error"
            outlined
            title={t(`${prefix}.error-title`)}
            extraContent={
              <div className={styles.packageAnnualSchedule_notificationAction}>
                <Link
                  size="sm"
                  iconLeft="refresh"
                  role="button"
                  onClick={() => void refresh()}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void refresh();
                    }
                  }}
                >
                  {t(`${prefix}.retry`)}
                </Link>
              </div>
            }
          >
            {t(`${prefix}.error`)}
          </CardNotification>
        </div>
      ) : !groups.length ? (
        <CardNotification state="info" outlined title={t(`${prefix}.empty-configuration-title`)}>
          {t(`${prefix}.empty-configuration`)}
        </CardNotification>
      ) : !weeks.length ? (
        <CardNotification state="info" outlined>
          {t(`${prefix}.empty-period`)}
        </CardNotification>
      ) : (
        <>
          <AnnualScheduleMatrix
            groups={paginated.groups}
            weeks={weeks}
            onSelectCell={store.selectCell}
          />
          <Pager
            classname={{ global: styles.packageAnnualSchedule_pagination }}
            page={paginated.page}
            size={pageSize}
            total={paginated.total}
            onChange={store.setPage}
            showRowsPerPage
            pageSizeOptions={[5, 10, 20]}
            labelRowsPerPage={t(`${prefix}.leagues-per-page`)}
            onRowsPerPageChange={option => store.setPageSize(option.value)}
          />
        </>
      )}
      {/* Keep the Drawer mounted while closed so Avocado can play its opening transition. */}
      <AnnualScheduleDrawer
        isOpen={isDrawerOpen}
        context={context}
        scopeKey={scopeKey}
        selection={selectedCell}
        group={group}
        week={week}
        onClose={closeDrawer}
        onEmpty={refresh}
      />
    </section>
  );
}
